import React, { useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import UserSearchModal, {
  type User,
} from '../../../Components/UserSearchModal';
import type { FoundationFormQuestion } from '../../../endpoints/foundation';

type StatusFilter =
  | 'active'
  | 'inactive'
  | 'all-members'
  | 'deceased'
  | 'non-members'
  | 'all';

const STATUS_FILTERS: StatusFilter[] = [
  'active',
  'inactive',
  'all-members',
  'deceased',
  'non-members',
  'all',
];

export type UserSelectValue = {
  user_id: string;
  name: string;
};

function parseUserSelect(raw: string): UserSelectValue | null {
  const s = (raw || '').trim();
  if (!s) return null;
  try {
    const obj = JSON.parse(s) as { user_id?: unknown; name?: unknown };
    const userId = String(obj?.user_id || '').trim();
    const name = String(obj?.name || '').trim();
    if (!userId) return null;
    return { user_id: userId, name };
  } catch {
    return null;
  }
}

function statusFromOptions(q: FoundationFormQuestion): StatusFilter {
  const raw = q.options_json as Record<string, unknown> | undefined;
  const status = String(raw?.status || '').trim();
  if (STATUS_FILTERS.includes(status as StatusFilter)) {
    return status as StatusFilter;
  }
  return 'all-members';
}

type Props = {
  question: FoundationFormQuestion;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
};

export default function UserSelectField({
  question,
  value,
  onChange,
  disabled = false,
}: Props) {
  const [open, setOpen] = useState(false);
  const selected = parseUserSelect(value);
  const label = question.label;
  const helper = question.help_text;

  const applyUser = (user: User) => {
    const name =
      user.full_name ||
      `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
      user.id;
    onChange(JSON.stringify({ user_id: user.id, name }));
    setOpen(false);
  };

  return (
    <Stack spacing={0.5}>
      <Typography variant="body2">{label}</Typography>
      {helper && (
        <Typography variant="caption" color="text.secondary">
          {helper}
        </Typography>
      )}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
      >
        <Typography variant="body2">{selected?.name || 'None'}</Typography>
        {!disabled && (
          <>
            <Button size="small" onClick={() => setOpen(true)}>
              {selected ? 'Change' : 'Select member'}
            </Button>
            {selected && (
              <Button size="small" onClick={() => onChange('')}>
                Clear
              </Button>
            )}
          </>
        )}
      </Stack>
      <UserSearchModal
        modalOpen={open}
        setModalOpen={setOpen}
        setUser={applyUser}
        userObjectRet
        statusFilter={statusFromOptions(question)}
        allowSetToMe={false}
      />
    </Stack>
  );
}
